/**
 * AppearanceOutcomeControl — did the client actually turn up? (Lloyd, Aug 21 2026.)
 *
 * The question the failure-to-appear fee hangs on. Three deliberate constraints, each matching a
 * guard the backend enforces regardless:
 *
 *   · **It only shows where attendance was required.** Recording "did not appear" against a setting
 *     nobody had to attend would manufacture a failure to appear — a $350–$1,500 fee on the
 *     engagement agreement's terms.
 *   · **It only shows once the setting has passed.** A setting is not missed until the day it falls
 *     on is over; before then there is no outcome to record.
 *   · **"Did not appear" asks for confirmation**, because it is the answer that starts a fee, a bond
 *     and probably a warrant. Who answered is stamped by the server from the session.
 */
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CourtEventApi, {
  AppearanceOutcome,
  AppearanceOutcomeRefusedError,
  AppearanceRequirement,
  APPEARANCE_OUTCOME_LABELS,
  appearanceOutcomeApplies,
  appearanceSettingHasPassed,
} from '../../api/CourtEventApi';

interface AppearanceOutcomeControlProps {
  courtEventGuid: string;
  requirement: AppearanceRequirement | undefined | null;
  outcome: AppearanceOutcome | undefined;
  setBy?: string | null;
  setAt?: string | null;
  note?: string | null;
  /** Epoch ms of the setting. The control hides until the day it falls on is over. */
  startEpoch: number;
  isHearing?: boolean;
  onRecorded?: () => void;
  disabled?: boolean;
}

const AppearanceOutcomeControl: React.FC<AppearanceOutcomeControlProps> = ({
  courtEventGuid,
  requirement,
  outcome,
  setBy,
  setAt,
  note,
  startEpoch,
  isHearing = true,
  onRecorded,
  disabled = false,
}) => {
  const [pending, setPending] = useState<AppearanceOutcome | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // The two conditions the backend also enforces. If either is false there is nothing to answer.
  if (!isHearing || !appearanceOutcomeApplies(requirement)) return null;
  if (!appearanceSettingHasPassed(startEpoch)) {
    return (
      <Typography variant="caption" color="text.secondary">
        Whether the client appeared can be recorded once this setting has passed.
      </Typography>
    );
  }

  const current: AppearanceOutcome = outcome ?? 'UNRECORDED';
  const recorded = current !== 'UNRECORDED' && Boolean(setAt);

  const apply = async (next: AppearanceOutcome) => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await CourtEventApi.recordAppearanceOutcome({
        CourtEvent_guid: courtEventGuid,
        CourtEvent_AppearanceOutcome: next,
        CourtEvent_AppearanceOutcomeNote: noteDraft.trim() || null,
      });
      setNotice('Saved.');
      setPending(null);
      setNoteDraft('');
      onRecorded?.();
    } catch (err) {
      setError(
        err instanceof AppearanceOutcomeRefusedError || err instanceof Error
          ? err.message
          : 'Could not save that.',
      );
    } finally {
      setSaving(false);
    }
  };

  const onSelect = (next: AppearanceOutcome) => {
    if (next === current) return;
    // "Did not appear" always confirms; the others save straight away.
    if (next === 'DID_NOT_APPEAR') {
      setNoteDraft(note ?? '');
      setPending(next);
    } else {
      apply(next);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
        Did the client appear?
      </Typography>
      <FormControl size="small" fullWidth disabled={disabled || saving}>
        <Select
          value={current}
          onChange={(e) => onSelect(e.target.value as AppearanceOutcome)}
          renderValue={(selected) => APPEARANCE_OUTCOME_LABELS[selected as AppearanceOutcome]}
        >
          {(Object.keys(APPEARANCE_OUTCOME_LABELS) as AppearanceOutcome[]).map((key) => (
            <MenuItem key={key} value={key}>
              {APPEARANCE_OUTCOME_LABELS[key]}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Box sx={{ mt: 0.5 }}>
        {recorded ? (
          <Chip
            size="small"
            color={current === 'DID_NOT_APPEAR' ? 'error' : 'success'}
            variant="outlined"
            label={`${APPEARANCE_OUTCOME_LABELS[current]} — recorded by ${setBy || 'staff'}${
              setAt ? ` on ${new Date(setAt).toLocaleDateString('en-US')}` : ''
            }`}
          />
        ) : (
          <Chip size="small" color="warning" variant="outlined" label="Nobody has recorded this yet" />
        )}
      </Box>

      {note && current === 'DID_NOT_APPEAR' && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Note: {note}
        </Typography>
      )}

      {notice && (
        <Alert severity="info" sx={{ mt: 1 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Dialog open={pending !== null} onClose={() => !saving && setPending(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Record that the client did not appear</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            A failure to appear starts a fee under the engagement agreement, and usually a bond and a
            warrant. Only record this once you are sure the client was required to attend and did
            not.
          </Alert>
          <TextField
            label="Note (optional)"
            fullWidth
            multiline
            minRows={2}
            size="small"
            value={noteDraft}
            onChange={(e) => setNoteDraft(e.target.value)}
            placeholder="What is known — e.g. court confirmed no appearance, warrant issued"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPending(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={saving}
            onClick={() => pending && apply(pending)}
          >
            {saving ? 'Saving…' : 'Record — did not appear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppearanceOutcomeControl;
